import React, { useRef, useEffect, useState } from 'react';
import { Button, FormField, Icon, P } from '@fashionnovateam/nova-ui-toolkit';
import { useFormContext, Controller } from 'react-hook-form';
import { ControlledFormInput } from '../../../../Lib/Utils/FormUtils/ControlledFormInput';
import { AddTagFormData } from '../types';
import { FILE_VALIDATION } from '../constants';
import { SampleTagImage } from '../../SampleTagImage/SampleTagImage';

export function ProductImageSection({
  showWrapper = true,
  disableStyleNumber = false,
}: {
  showWrapper?: boolean;
  disableStyleNumber?: boolean;
}) {
  const { control, setValue, watch } = useFormContext<AddTagFormData>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const productImageUrl = watch('productImageUrl');
  const [uploadError, setUploadError] = useState<string | null>(null);

  useEffect(
    () => () => {
      if (productImageUrl && productImageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(productImageUrl);
        setValue('productImageUrl', undefined, { shouldValidate: false });
      }
    },
    [],
  );

  const handleFileSelect = (file: File) => {
    setUploadError(null);

    if (!FILE_VALIDATION.ACCEPTED_TYPES.includes(file.type as any)) {
      setUploadError(
        'Invalid file type. Please upload JPEG or PNG images only.',
      );
      return;
    }

    if (file.size > FILE_VALIDATION.MAX_SIZE) {
      setUploadError(
        `File too large. Please upload images smaller than ${FILE_VALIDATION.MAX_SIZE_LABEL}.`,
      );
      return;
    }

    if (productImageUrl && productImageUrl.startsWith('blob:')) {
      URL.revokeObjectURL(productImageUrl);
    }

    const blobUrl = URL.createObjectURL(file);
    setValue('productImageFile', file, { shouldValidate: true });
    setValue('productImageUrl', blobUrl, { shouldValidate: true });
  };

  const handleRemoveImage = (currentImageUrl?: string) => {
    setValue('productImageUrl', '', { shouldValidate: true });
    setValue('productImageFile', undefined, { shouldValidate: true });

    if (currentImageUrl && currentImageUrl.startsWith('blob:')) {
      URL.revokeObjectURL(currentImageUrl);
    }
  };

  const handleAttachFileClick = (e: React.MouseEvent) => {
    e.preventDefault();
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const renderImage = (imageUrl: string) => {
    if (imageUrl.startsWith('blob:')) {
      return (
        <img
          src={imageUrl}
          alt="Product preview"
          className="w-32 h-32 object-cover rounded-lg border border-neutral-200 shadow-sm pointer-events-none"
          onError={(e) => {
            console.error('Failed to load blob image:', imageUrl);
            e.currentTarget.style.display = 'none';
          }}
        />
      );
    }
    return (
      <SampleTagImage
        productImage={imageUrl}
        alt="Product preview"
        className="w-32 h-32 object-cover rounded-lg border border-neutral-200 shadow-sm pointer-events-none"
        onError={(e) => {
          console.error('Failed to load image:', imageUrl);
          e.currentTarget.style.display = 'none';
        }}
      />
    );
  };

  const content = (
    <>
      <Controller
        control={control}
        name="productImageUrl"
        rules={{
          required: 'Product image is required',
        }}
        render={({ field, fieldState: { error } }) => (
          <div className="space-y-4">
            <input
              ref={fileInputRef}
              type="file"
              accept={FILE_VALIDATION.ACCEPTED_TYPES.join(',')}
              className="hidden"
              onChange={handleFileInputChange}
            />

            <FormField
              label="Add Product Image"
              required
              error={error?.message}
              inputId=""
            >
              <div className="space-y-4">
                {field.value && (
                  <div className="space-y-3">
                    <div className="relative inline-block">
                      <div className="pointer-events-none">
                        {renderImage(field.value)}
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          handleRemoveImage(field.value);
                        }}
                        className="absolute -top-2 -right-2 rounded-full w-6 h-6 border border-black flex items-center justify-center z-10"
                        title="Remove image"
                      >
                        <P size="xs" weight="semibold">
                          ×
                        </P>
                      </button>
                    </div>
                    <Button
                      type="button"
                      color="secondary"
                      size="sm"
                      onClick={handleAttachFileClick}
                      className="flex items-center gap-2"
                    >
                      <Icon variant="stroke" name="paperclip/paperclip-slant" />
                      Replace Image
                    </Button>
                  </div>
                )}

                {!field.value && (
                  <Button
                    type="button"
                    color="secondary"
                    size="md"
                    onClick={handleAttachFileClick}
                    className="flex items-center gap-2"
                  >
                    <Icon variant="stroke" name="paperclip/paperclip-slant" />
                    Attach File
                  </Button>
                )}

                {uploadError && (
                  <div className="text-red-600 text-sm">{uploadError}</div>
                )}

                <P size="xs" color="disabled">
                  Upload a product image (JPEG/PNG/JPG, max{' '}
                  {FILE_VALIDATION.MAX_SIZE_LABEL}). This image will be
                  displayed on the sample tag.
                </P>
              </div>
            </FormField>
          </div>
        )}
      />

      {!disableStyleNumber && (
        <ControlledFormInput
          name="styleNumber"
          label="Style #"
          control={control}
          inputType="text"
          required
          inputProps={{
            placeholder: 'Add',
            className: 'w-full',
          }}
        />
      )}
    </>
  );

  return showWrapper ? (
    <div className="rounded-xl border border-neutral-200 shadow-sm p-5 space-y-4">
      {content}
    </div>
  ) : (
    <div className="space-y-4">{content}</div>
  );
}
